/**
 * DocFreshnessSettings — per-company admin config for the Document
 * Freshness workflow gate.
 *
 * Lives under Workspace nav → "Doc freshness alerts" (employer + admin).
 * Backed by GET/PATCH /api/settings/doc-freshness.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Save, ArrowLeft, ShieldAlert } from "lucide-react";

const SLOT_META = [
    { key: "joining_letter",    label: "Joining letter" },
    { key: "relieving_letter",  label: "Relieving letter" },
    { key: "experience_letter", label: "Experience letter" },
];

const PERSONAL_SLOT_META = [
    { key: "pan",               label: "PAN card" },
    { key: "aadhaar",           label: "Aadhaar" },
    { key: "passport",          label: "Passport" },
    { key: "address_proof",     label: "Address proof" },
    { key: "offer_acceptance",  label: "Offer acceptance" },
    { key: "bank_details",      label: "Bank details" },
];

export default function DocFreshnessSettings() {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);
    const [newStage, setNewStage] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/settings/doc-freshness");
                setCfg(data);
            } catch (err) { toast.error(formatApiError(err)); }
        })();
    }, []);

    if (!cfg) return <div className="p-6">Loading…</div>;

    const patch = async (updates) => {
        setSaving(true);
        try {
            const { data } = await api.patch("/settings/doc-freshness", updates);
            setCfg(data);
            toast.success("Saved");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const toggleSlot = (slot) => {
        const cur = new Set(cfg.required_slots || []);
        if (cur.has(slot)) cur.delete(slot);
        else cur.add(slot);
        patch({ required_slots: Array.from(cur) });
    };

    const togglePersonalSlot = (slot) => {
        const cur = new Set(cfg.personal_slots || []);
        if (cur.has(slot)) cur.delete(slot);
        else cur.add(slot);
        patch({ personal_slots: Array.from(cur) });
    };

    const addStage = () => {
        const s = newStage.trim();
        if (!s) return;
        const cur = new Set(cfg.gate_stages || []);
        cur.add(s);
        patch({ gate_stages: Array.from(cur) });
        setNewStage("");
    };

    const removeStage = (s) => {
        const cur = (cfg.gate_stages || []).filter((x) => x !== s);
        patch({ gate_stages: cur });
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto space-y-6" data-testid="doc-freshness-settings">
            <div>
                <Link to="/company" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-4 w-4" /> Back to Company
                </Link>
                <h1 className="text-2xl sm:text-4xl font-bold mt-2 flex items-center gap-2">
                    <ShieldAlert className="h-7 w-7 text-amber-600" />
                    Doc Freshness Alerts
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Turn per-experience employment documents (Joining / Relieving / Experience letter) into a
                    workflow gate. Recruiters get a warning — or a hard block — when they try to move a
                    candidate past a configured stage with missing docs.
                </p>
            </div>

            {/* Enable toggle */}
            <section className="border border-border rounded p-4 flex items-center justify-between gap-4">
                <div>
                    <div className="font-semibold">Enable gate</div>
                    <div className="text-xs text-muted-foreground">When off, no checks are enforced anywhere.</div>
                </div>
                <Switch
                    data-testid="df-enabled"
                    checked={!!cfg.enabled}
                    onCheckedChange={(v) => patch({ enabled: !!v })}
                    disabled={saving}
                />
            </section>

            {/* Enforcement */}
            <section className="border border-border rounded p-4 space-y-3">
                <div>
                    <div className="font-semibold">Enforcement</div>
                    <div className="text-xs text-muted-foreground">
                        <span className="font-medium">Warn</span> — recruiter can proceed (activity log records it).{" "}
                        <span className="font-medium">Block</span> — recruiter must upload docs or type an override reason.
                    </div>
                </div>
                <div className="flex gap-2">
                    {["warn", "block"].map((m) => (
                        <Button
                            key={m}
                            data-testid={`df-enforcement-${m}`}
                            variant={cfg.enforcement === m ? "default" : "outline"}
                            size="sm"
                            onClick={() => patch({ enforcement: m })}
                            disabled={saving}
                        >
                            {m === "warn" ? "Warn only" : "Hard block"}
                        </Button>
                    ))}
                </div>
            </section>

            {/* Scope */}
            <section className="border border-border rounded p-4 space-y-3">
                <div>
                    <div className="font-semibold">Which experience rows to check?</div>
                    <div className="text-xs text-muted-foreground">
                        Every candidate has multiple work-experience rows. Choose which ones need documents at the gate.
                    </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {[
                        { v: "latest_employer", label: "Latest employer only (recommended)" },
                        { v: "all",             label: "All experience rows" },
                    ].map((o) => (
                        <Button
                            key={o.v}
                            data-testid={`df-scope-${o.v}`}
                            variant={cfg.scope === o.v ? "default" : "outline"}
                            size="sm"
                            onClick={() => patch({ scope: o.v })}
                            disabled={saving}
                        >
                            {o.label}
                        </Button>
                    ))}
                </div>
            </section>

            {/* Required slots */}
            <section className="border border-border rounded p-4 space-y-3">
                <div>
                    <div className="font-semibold">Required documents</div>
                    <div className="text-xs text-muted-foreground">
                        These slot types (per experience row) must be filled to clear the gate.
                    </div>
                </div>
                <div className="space-y-2">
                    {SLOT_META.map((s) => (
                        <label key={s.key} className="flex items-center gap-3 text-sm cursor-pointer">
                            <input
                                data-testid={`df-slot-${s.key}`}
                                type="checkbox"
                                checked={(cfg.required_slots || []).includes(s.key)}
                                onChange={() => toggleSlot(s.key)}
                                disabled={saving}
                                className="h-4 w-4"
                            />
                            {s.label}
                        </label>
                    ))}
                </div>
            </section>

            {/* Gate stages */}
            <section className="border border-border rounded p-4 space-y-3">
                <div>
                    <div className="font-semibold">Gate stages</div>
                    <div className="text-xs text-muted-foreground">
                        Stage names that trigger the gate (case-insensitive). Match your pipeline stages exactly.
                    </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {(cfg.gate_stages || []).map((s) => (
                        <span
                            key={s}
                            data-testid={`df-stage-${s}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-amber-50 border border-amber-200"
                        >
                            {s}
                            <button
                                type="button"
                                data-testid={`df-stage-remove-${s}`}
                                onClick={() => removeStage(s)}
                                className="ml-1 text-amber-800 hover:text-red-600"
                                aria-label={`Remove ${s}`}
                            >×</button>
                        </span>
                    ))}
                    {(cfg.gate_stages || []).length === 0 && (
                        <span className="text-xs text-muted-foreground italic">No gate stages set.</span>
                    )}
                </div>
                <div className="flex gap-2">
                    <Input
                        data-testid="df-new-stage"
                        placeholder="e.g. Offer"
                        value={newStage}
                        onChange={(e) => setNewStage(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addStage(); } }}
                    />
                    <Button data-testid="df-add-stage" onClick={addStage} disabled={saving || !newStage.trim()}>
                        <Save className="h-4 w-4 mr-1" /> Add
                    </Button>
                </div>
            </section>

            <div className="text-xs text-muted-foreground">
                Missing-docs candidates are surfaced on{" "}
                <Link to="/docs-pending" className="text-ikb underline">/docs-pending</Link> and as a chip on the Candidates list.
            </div>

            {/* Personal / onboarding docs */}
            <section className="border border-border rounded p-4 space-y-3">
                <div>
                    <div className="font-semibold">Personal / onboarding documents</div>
                    <div className="text-xs text-muted-foreground">
                        Government IDs, bank details, offer acceptance — the day-one paperwork every joiner needs.
                        Missing personal docs also block/warn on gate stages.
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {PERSONAL_SLOT_META.map((s) => (
                        <label key={s.key} className="flex items-center gap-3 text-sm cursor-pointer">
                            <input
                                data-testid={`df-personal-slot-${s.key}`}
                                type="checkbox"
                                checked={(cfg.personal_slots || []).includes(s.key)}
                                onChange={() => togglePersonalSlot(s.key)}
                                disabled={saving}
                                className="h-4 w-4"
                            />
                            {s.label}
                        </label>
                    ))}
                </div>
            </section>

            {/* Auto-nudge */}
            <section className="border border-border rounded p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="font-semibold">Auto-nudge candidate</div>
                        <div className="text-xs text-muted-foreground">
                            Automatically email the candidate a reminder when they've been at a gate stage with missing
                            docs for the configured number of hours.
                        </div>
                    </div>
                    <Switch
                        data-testid="df-auto-nudge-enabled"
                        checked={cfg.auto_nudge_enabled !== false}
                        onCheckedChange={(v) => patch({ auto_nudge_enabled: !!v })}
                        disabled={saving}
                    />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label htmlFor="auto-nudge-hours" className="text-xs">Hours to wait</Label>
                        <Input
                            id="auto-nudge-hours"
                            data-testid="df-auto-nudge-hours"
                            type="number"
                            min={1}
                            max={720}
                            defaultValue={cfg.auto_nudge_hours || 48}
                            onBlur={(e) => {
                                const v = Number(e.target.value);
                                if (v && v !== cfg.auto_nudge_hours) patch({ auto_nudge_hours: v });
                            }}
                            disabled={saving}
                        />
                    </div>
                    <div>
                        <Label htmlFor="auto-nudge-max" className="text-xs">Max total nudges</Label>
                        <Input
                            id="auto-nudge-max"
                            data-testid="df-auto-nudge-max"
                            type="number"
                            min={0}
                            max={10}
                            defaultValue={cfg.auto_nudge_max ?? 2}
                            onBlur={(e) => {
                                const v = Number(e.target.value);
                                if (v !== cfg.auto_nudge_max) patch({ auto_nudge_max: v });
                            }}
                            disabled={saving}
                        />
                    </div>
                </div>
                <div className="text-[11px] text-muted-foreground">
                    Cadence per user spec: every {cfg.auto_nudge_hours || 48}h, capped at {cfg.auto_nudge_max ?? 2} total.
                </div>
            </section>
        </div>
    );
}
